import {
  CoverageId,
  InsuranceCoverage,
  InsuranceProvider,
  isInsuranceCoverageRequest,
  PopulatedAssessment,
  User,
} from '../../types'
import { FieldMap, Form } from '../../types/forms'
import {
  getHasPrimaryCoverage,
  getHasSecondaryCoverage,
  getPrimaryIsMedicaid,
  getSecondaryIsMedicaid,
} from '../../utils/assessments/coverage'

import { capitalizeFirstLetter } from '../../utils/data'
import { defaultStageValidate } from '../../utils/fields'

import callDetails from './callDetails'
import callInDirections from './callInDirections'
import inNetwork from './inNetwork'
// import maximumReimbursableCharges from './maximumReimbursableCharges'
import { get as nestedGet } from 'nested-property'
import { defaultFormValidate } from '../../utils/forms'
import outOfNetwork from './outOfNetwork'
import planCoversMidwives from './planCoversMidwives'
import { planDesignAndStateMandatesField } from './planDesignAndStateMandates'
// import reimbursementOption from './reimbursementOption'
export const callIn: Form = {
  name: 'Call-In Questions for your Insurer',
  path: 'insuranceInfo.primaryCoverage',
  pathName: 'Primary Coverage',
  description:
    "We'll guide you though a call with your insurance provider to get more information on your coverage",
  stages: {
    directions: callInDirections,
    'in-network': inNetwork,
    'out-of-network': outOfNetwork,
    // these two were moved to out of network
    // 'reimbursement-option': reimbursementOption,
    // 'maximum-reimbursable-charges': maximumReimbursableCharges,
    'plan-covers-midwives': planCoversMidwives,
    'plan-design-and-state-mandates': planDesignAndStateMandatesField,
    'call-details': callDetails,
  },
}

export const optInCallInForm: Form = {
  name: 'Insurance Call-In',
  path: 'insuranceInfo.primaryCoverage',
  pathName: 'Primary Coverage',
  description:
    'We will guide you through a call with your insurance provider to get more information on your coverage',
  stages: {
    callInDirections,
    'in-network': inNetwork,
    'out-of-network': outOfNetwork,
    'plan-covers-midwives': planCoversMidwives,
    'plan-design-and-state-mandates': planDesignAndStateMandatesField,
    'call-details': callDetails,
  },
}

export const callInField: FieldMap = {
  name: 'Call In Questions',
  children: callIn.stages,
}

export const optInCallInField: FieldMap = {
  name: 'Call In Questions',
  children: optInCallInForm.stages,
}

export const getCallInForm = (
  admin: boolean,
  assessment?: Omit<PopulatedAssessment, 'availableForms' | 'stages' | 'plans'> | null,
): Form | null => {
  const returnForm = (coverageId: CoverageId, coverage: InsuranceCoverage) => ({
    ...optInCallInForm,
    name: `Call-In Questions for ${coverage?.insuranceProvider?.name} - ${coverage?.memberId ? `Member ID: ${coverage.memberId}` : 'No Member ID'}`,
    path: `insuranceInfo.${coverageId}Coverage`,
    pathName: `${capitalizeFirstLetter(coverageId)} Coverage`,
  })

  const insuranceInfo = admin
    ? assessment?.mergedData?.insuranceInfo
    : assessment?.data?.insuranceInfo

  const hasPrimaryCoverage = getHasPrimaryCoverage(insuranceInfo ?? {})
  if (!hasPrimaryCoverage) return null
  const primaryInsurer = (insuranceInfo?.primaryCoverage?.insuranceProvider ??
    null) as InsuranceProvider | null
  const primaryIsMedicaid = getPrimaryIsMedicaid(insuranceInfo ?? {})
  if (!primaryIsMedicaid && primaryInsurer && !primaryInsurer.loqNotRequired)
    return returnForm('primary', insuranceInfo?.primaryCoverage)
  const hasSecondaryCoverage = getHasSecondaryCoverage(insuranceInfo ?? {})
  if (!hasSecondaryCoverage) return null
  const secondaryInsurer = (insuranceInfo?.secondaryCoverage?.insuranceProvider ??
    null) as InsuranceProvider | null
  const secondaryIsMedicaid = getSecondaryIsMedicaid(insuranceInfo ?? {})
  if (!secondaryIsMedicaid && secondaryInsurer && !secondaryInsurer.loqNotRequired)
    return returnForm('secondary', insuranceInfo?.secondaryCoverage)
  return null
}

export const getAdditionalPlansRequested = (
  user: User | null | undefined,
  // assessment: Assessment | PopulatedAssessment | undefined,
) => {
  const { insurancePlans } = user ?? {}
  const { additional } = insurancePlans ?? {}
  return Object.values(additional ?? {}).some(p => {
    if (isInsuranceCoverageRequest(p)) return true
    if (p.isMedicaid) return false
    const planCallInErrors = defaultStageValidate(callInField, true)(p)
    return !!planCallInErrors && !!Object.keys(p.callInRequests ?? {}).length
  })
}

export const getInsuranceCallInRequired = (assessment: PopulatedAssessment) => {
  if (!assessment?.summarySubmittedOn) return false
  if (assessment?.insurerCallStatus === 'opted-out') return false
  const insuranceCallInForm = getCallInForm(false, assessment)
  if (!insuranceCallInForm) return false
  return true
}

export const getInsuranceCallInComplete = (assessment: PopulatedAssessment) => {
  // if (assessment.sentOn && assessment.sentOn < v106releaseDate) return true
  if (!assessment.insurerCallStatus) return false
  if (assessment?.insurerCallStatus === 'opted-out') return true
  return !!assessment.insurerCallInSubmittedOn
}

export const validateAssessmentCallInForm = (assessment: PopulatedAssessment) => {
  const form = getCallInForm(false, assessment)
  if (!form) return undefined
  const data = form.path ? nestedGet(assessment.data, form.path) : assessment.data
  return defaultFormValidate(form, true)(data)
}
export const getInsuranceCallInFormComplete = (assessment: PopulatedAssessment) =>
  !validateAssessmentCallInForm(assessment)
